import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CmsService } from '../services/cms';
import { resolveUploadsUrl } from '../utils/resolveUploadsUrl';

const MOBILE_STUDY_LINKS = [
  { to: '/study-in-uk', label: 'Study in UK' },
  { to: '/study-in-usa', label: 'Study in USA' },
  { to: '/study-in-canada', label: 'Study in Canada' },
  { to: '/study-in-australia', label: 'Study in Australia' },
  { to: '/study-in-new-zealand', label: 'Study in New Zealand' },
  { to: '/study-in-uae', label: 'Study in Dubai (UAE)' },
  { to: '/study-in-ireland', label: 'Study in Ireland' },
  { to: '/study-in-germany', label: 'Study in Germany' },
  { to: '/study-in-europe', label: 'Study in Europe' },
];

// Countries in the header flag dropdown — every destination that has its own
// study-in page (Ireland, Germany and Europe in the mega-menu have no page yet).
// The flag PNGs pad the same 32x24 flag differently (UK/USA/Canada sit inside
// white/transparent margins, the others fill the file), so `crop` records where
// the flag sits in each file — [x, y, fileWidth, fileHeight] — and CountryFlag
// trims to it, making every flag render at exactly the same size.
const COUNTRY_LINKS = [
  { to: '/study-in-uk', label: 'UK', flag: '/images/icons/uk-logo.png', crop: [8, 12, 48, 48] },
  { to: '/study-in-usa', label: 'USA', flag: '/images/icons/usa-logo.png', crop: [8, 12, 48, 48] },
  { to: '/study-in-canada', label: 'Canada', flag: '/images/icons/ca-logo.png', crop: [4, 8, 40, 40] },
  { to: '/study-in-australia', label: 'Australia', flag: '/images/icons/aus-logo.png', crop: [0, 0, 48, 24] },
  { to: '/study-in-new-zealand', label: 'New Zealand', flag: '/images/icons/nz-logo.png', crop: [0, 0, 32, 24] },
  { to: '/study-in-uae', label: 'Dubai (UAE)', flag: '/images/icons/uae-logo.png', crop: [0, 0, 32, 24] },
];

// Renders just the 32x24 flag area of a country's PNG, `height` px tall (4:3).
const CountryFlag = ({ country, height, alt = '' }) => {
  const [x, y, fileW, fileH] = country.crop;
  const scale = height / 24;
  return (
    <span
      className="relative inline-block shrink-0 overflow-hidden rounded-[2px]"
      style={{ width: height * (4 / 3), height }}
    >
      <img
        src={country.flag}
        alt={alt}
        className="absolute max-w-none"
        style={{ width: fileW * scale, height: fileH * scale, left: -x * scale, top: -y * scale }}
      />
    </span>
  );
};

const MOBILE_COACHING_LINKS = [
  { to: '/coaching/ielts', label: 'IELTS Coaching' },
  { to: '/coaching/pte', label: 'PTE Coaching' },
  { to: '/coaching/toefl', label: 'TOEFL iBT Coaching' },
  { to: '/coaching/celpip', label: 'CELPIP Coaching' },
  { to: '/coaching/duolingo', label: 'DUOLINGO Coaching' },
  { to: '/coaching/inter-preparatory', label: 'Inter. Preparatory' },
  { to: '/coaching/sat', label: 'SAT Coaching' },
  { to: '/coaching/gre', label: 'GRE Coaching' },
  { to: '/coaching/gmat', label: 'GMAT Coaching' },
  { to: '/coaching/french', label: 'French Language' },
  { to: '/coaching/german', label: 'German Language' },
  { to: '/coaching/japanese', label: 'Japanese Language' },
];

const MOBILE_SERVICE_LABELS = [
  { label: 'Education Loan', to: '/services/education-loan' },
  { label: 'Statement of Purpose', to: '/services/sop' },
  { label: 'Sim Card', to: '/services/sim-card' },
  { label: 'Scholarship', to: '/services/scholarship' },
  { label: 'Psychometric Test', to: null },
  { label: 'Air tickets', to: '/services/air-ticket' },
  { label: 'Insurance', to: '/services/insurance' },
  { label: 'Visa Assistance', to: '/services/visa-service' },
  { label: 'Forex', to: '/services/forex' },
];

const MOBILE_COMPANY_LINKS = [
  { to: '/about-us', label: 'About Us' },
  { to: '/career', label: 'Career' },
  { to: '/contact-us', label: 'Contact Us' },
  { to: '/news', label: 'News' },
  { to: '/blogs', label: 'Blogs' },
  { to: '/events', label: 'Events' },
  { to: '/webinar', label: 'Webinar' },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mobileAccordion, setMobileAccordion] = useState(null);
  const [studyOpen, setStudyOpen] = useState(false);
  const [coachingOpen, setCoachingOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);
  const countryMenuRef = useRef(null);
  const [latestPosts, setLatestPosts] = useState({ news: null, blog: null, event: null, webinar: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const types = ['news', 'blog', 'event', 'webinar'];
      const results = await Promise.allSettled(types.map((t) => CmsService.listPublicPosts(t, 1)));
      if (cancelled) return;
      const next = {};
      types.forEach((type, i) => {
        const result = results[i];
        const post = result.status === 'fulfilled' && Array.isArray(result.value?.data) ? result.value.data[0] : null;
        next[type] = post || null;
      });
      setLatestPosts(next);
    })();
    return () => { cancelled = true; };
  }, []);
  const navigate = useNavigate();
  const { isAuthenticated, hasRole } = useAuth();
  const isAdminOrInstructor = isAuthenticated && (hasRole('admin') || hasRole('instructor'));
  const location = useLocation();
  const [showHeader, setShowHeader] = useState(true);

  // Close the mobile menu and any open desktop dropdown whenever the route
  // changes (e.g. after tapping/clicking a link inside one) — otherwise the
  // dropdown stays rendered on top of the newly-navigated page.
  useEffect(() => {
    setIsMenuOpen(false);
    setMobileAccordion(null);
    setStudyOpen(false);
    setCoachingOpen(false);
    setServicesOpen(false);
    setCompanyOpen(false);
    setCountryOpen(false);
  }, [location.pathname]);

  // Close the country dropdown on any click outside it, or on Escape.
  useEffect(() => {
    if (!countryOpen) return undefined;
    const onPointerDown = (e) => {
      if (countryMenuRef.current && !countryMenuRef.current.contains(e.target)) setCountryOpen(false);
    };
    const onKeyDown = (e) => { if (e.key === 'Escape') setCountryOpen(false); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [countryOpen]);

  useEffect(() => {
    if (location.pathname !== '/' && location.pathname !== '/home') {
      setShowHeader(true);
      return;
    }

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      // Hide header when video is reveal-fullscreen animation
      // (Approximate revealStart to exitEnd scroll range)
      if (scrollY > 10 && scrollY < viewportHeight * 0.3) {
        setShowHeader(false);
      } else {
        setShowHeader(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const isServicePage = location.pathname.startsWith('/services/');
  // Show the flag of the country page being viewed; USA everywhere else.
  const currentCountry = COUNTRY_LINKS.find((c) => location.pathname.startsWith(c.to))
    || COUNTRY_LINKS.find((c) => c.to === '/study-in-usa');

  return (
      <nav className={`fixed ${isServicePage ? 'top-6 md:top-10' : 'top-2 md:top-6'} left-0 right-0 z-[160] w-full transition-all duration-300 overflow-visible ${showHeader ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'}`}>
      <div className="w-full px-3 md:px-4">
        <div className="flex justify-center items-center h-16">
          {/* Unified Rounded Menu Bar - Desktop Only */}
          <div className="hidden xl:flex items-center rounded-full bg-white/90 px-7 lg:px-8 py-3 shadow-lg border border-white/70 relative z-50 w-full max-w-[98%] mx-auto">
            {/* Logo */}
            <div className="flex shrink-0 items-center">
              <img
                src="/images/Logo-EduGen.svg"
                alt="Edugen Logo"
                className="h-9 lg:h-10 w-auto"
              />
            </div>

            {/* Navigation Links */}
            <div className="ml-10 lg:ml-12 flex flex-1 items-center justify-start space-x-7 xl:space-x-8">
              <Link to="/" className="text-[#173d38] hover:text-orange-500 transition-colors" aria-label="Home">
                <svg className="h-7 w-7" viewBox="0 0 28 28" fill="none" aria-hidden="true">
                  <path d="M4.5 12.4 14 4.7l9.5 7.7v11.1a1.5 1.5 0 0 1-1.5 1.5h-5.6v-6.6a2.4 2.4 0 0 0-4.8 0V25H6a1.5 1.5 0 0 1-1.5-1.5V12.4Z" fill="currentColor" />
                  <path d="M11.2 25v-6.5a2.8 2.8 0 0 1 5.6 0V25" fill="#f47f3be0" />
                  <path d="M2.8 13.4 14 4.4l11.2 9" stroke="#111827" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
              
              <div
                className="relative group flex items-center cursor-pointer"
                onMouseEnter={() => { setStudyOpen(true); setCoachingOpen(false); setServicesOpen(false); setCompanyOpen(false); }}
                onClick={() => setStudyOpen(v => !v)}
              >
                <span className="text-gray-900 font-semibold text-base hover:text-orange-500 transition-colors">Study Abroad</span>
                <svg className="ml-3 h-4 w-4 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              <div
                className="relative group flex items-center cursor-pointer"
                onMouseEnter={() => { setCoachingOpen(true); setStudyOpen(false); setServicesOpen(false); setCompanyOpen(false); }}
                onClick={() => setCoachingOpen(v => !v)}
              >
                <span className="text-gray-900 font-semibold text-base hover:text-orange-500 transition-colors">Coaching</span>
                <svg className="ml-3 h-4 w-4 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              <div
                className="relative group flex items-center cursor-pointer"
                onMouseEnter={() => { setServicesOpen(true); setStudyOpen(false); setCoachingOpen(false); setCompanyOpen(false); }}
                onClick={() => setServicesOpen(v => !v)}
              >
                <span className="text-gray-900 font-semibold text-base hover:text-orange-500 transition-colors">Services</span>
                <svg className="ml-3 h-4 w-4 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              <div
                className="relative group flex items-center cursor-pointer"
                onMouseEnter={() => { setCompanyOpen(true); setStudyOpen(false); setCoachingOpen(false); setServicesOpen(false); }}
                onClick={() => setCompanyOpen(v => !v)}
              >
                <span className="text-gray-900 font-semibold text-base hover:text-orange-500 transition-colors">Company</span>
                <svg className="ml-3 h-4 w-4 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            
            {/* Right Side Actions */}
            <div className="ml-auto flex shrink-0 items-center space-x-4">
              <Link to="/courses" className="flex items-center px-6 py-2 rounded-full bg-gradient-to-r from-[#F68870] to-[#E05C3E] text-white hover:opacity-90 font-medium text-sm transition-opacity shadow-sm">
                <span>Course Finder</span>
                <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </Link>

              {!isAuthenticated ? (
                <Link to="/login" className="flex items-center px-6 py-2 rounded-full border border-[#E05C3E] bg-white text-[#BF4528] hover:bg-orange-50 font-medium text-sm transition-colors">
                  <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 17l5-5-5-5" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H3" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 4h4a2 2 0 012 2v12a2 2 0 01-2 2h-4" />
                  </svg>
                  <span>Login</span>
                </Link>
              ) : (
                <button
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center px-6 py-2 rounded-full bg-orange-600 text-white hover:bg-orange-700 font-medium text-sm transition-colors shadow-sm"
                >
                  <span>Dashboard</span>
                </button>
              )}

              <div className="relative" ref={countryMenuRef}>
                <button
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={countryOpen}
                  aria-label={`Study destination: ${currentCountry.label}`}
                  onClick={() => {
                    setCountryOpen((v) => !v);
                    setStudyOpen(false); setCoachingOpen(false); setServicesOpen(false); setCompanyOpen(false);
                  }}
                  className="flex items-center space-x-2 rounded-lg px-1 py-1 hover:bg-orange-50 transition-colors"
                >
                  <CountryFlag country={currentCountry} height={12} alt={currentCountry.label} />
                  <svg className={`h-4 w-4 text-gray-600 transition-transform ${countryOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {countryOpen && (
                  <div role="menu" className="absolute right-0 top-full mt-4 w-60 rounded-2xl border border-gray-100 bg-white p-2 shadow-xl z-[70]">
                    <div className="px-3 pt-1 pb-2 text-xs font-semibold text-gray-500">STUDY DESTINATIONS</div>
                    {COUNTRY_LINKS.map((country) => {
                      const isActive = country.to === currentCountry.to && location.pathname.startsWith(country.to);
                      return (
                        <Link
                          key={country.to}
                          to={country.to}
                          role="menuitem"
                          onClick={() => setCountryOpen(false)}
                          className={`flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors ${isActive ? 'bg-orange-50 text-[#E05C3E] font-semibold' : 'text-[#0B1C33] hover:bg-orange-50 hover:text-[#E05C3E]'}`}
                        >
                          <CountryFlag country={country} height={10} />
                          <span>Study in {country.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Mobile/tablet header bar: logo + working hamburger toggle */}
          <div className="flex xl:hidden items-center justify-between rounded-full bg-white/90 px-4 sm:px-6 py-2.5 shadow-lg border border-white/70 w-full max-w-[98%] mx-auto">
            <Link to="/" className="flex shrink-0 items-center" aria-label="Edugen Home">
              <img src="/images/Logo-EduGen.svg" alt="Edugen Logo" className="h-8 sm:h-9 w-auto" />
            </Link>
            <button
              type="button"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
              className="text-gray-700 hover:text-orange-500 transition-colors p-2"
              onClick={() => setIsMenuOpen((v) => !v)}
            >
              {isMenuOpen ? (
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile/tablet dropdown menu panel */}
          {isMenuOpen && (
            <div className="xl:hidden absolute top-[72px] left-0 right-0 px-3 sm:px-4 z-[60]">
              <div className="bg-white rounded-[24px] shadow-2xl border border-gray-100 p-3 max-h-[calc(100vh-96px)] overflow-y-auto">
                <Link
                  to="/"
                  className="block px-4 py-3 rounded-xl font-semibold text-gray-900 hover:bg-orange-50 hover:text-orange-500 transition-colors"
                >
                  Home
                </Link>

                {[
                  { key: 'study', title: 'Study Abroad', links: MOBILE_STUDY_LINKS },
                  { key: 'coaching', title: 'Coaching', links: MOBILE_COACHING_LINKS },
                  { key: 'company', title: 'Company', links: MOBILE_COMPANY_LINKS },
                ].map((section) => (
                  <div key={section.key} className="border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => setMobileAccordion((v) => (v === section.key ? null : section.key))}
                      className="w-full flex items-center justify-between px-4 py-3 font-semibold text-gray-900 hover:text-orange-500 transition-colors"
                    >
                      {section.title}
                      <svg
                        className={`h-4 w-4 text-gray-600 transition-transform ${mobileAccordion === section.key ? 'rotate-180' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {mobileAccordion === section.key && (
                      <div className="pb-2 pl-2 space-y-1">
                        {section.links.map((link) => (
                          <Link
                            key={link.to}
                            to={link.to}
                            className="block px-4 py-2.5 rounded-xl text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-500 transition-colors"
                          >
                            {link.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                <div className="border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setMobileAccordion((v) => (v === 'services' ? null : 'services'))}
                    className="w-full flex items-center justify-between px-4 py-3 font-semibold text-gray-900 hover:text-orange-500 transition-colors"
                  >
                    Services
                    <svg
                      className={`h-4 w-4 text-gray-600 transition-transform ${mobileAccordion === 'services' ? 'rotate-180' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {mobileAccordion === 'services' && (
                    <div className="pb-2 pl-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {MOBILE_SERVICE_LABELS.map((service) => (
                        service.to ? (
                          <Link
                            key={service.label}
                            to={service.to}
                            className="block px-4 py-2.5 rounded-xl text-sm text-gray-500 hover:bg-orange-50 hover:text-[#0B1C33]"
                          >
                            {service.label}
                          </Link>
                        ) : (
                          <span key={service.label} className="block px-4 py-2.5 rounded-xl text-sm text-gray-500">
                            {service.label}
                          </span>
                        )
                      ))}
                    </div>
                  )}
                </div>

                <div className="border-t border-gray-100 pt-3 mt-1 px-2 space-y-2">
                  <Link
                    to="/courses"
                    className="flex items-center justify-center px-4 py-3 rounded-full bg-gradient-to-r from-[#F68870] to-[#E05C3E] text-white font-semibold text-sm"
                  >
                    Course Finder
                  </Link>
                  {!isAuthenticated ? (
                    <Link
                      to="/login"
                      className="flex items-center justify-center px-4 py-3 rounded-full border border-[#E05C3E] bg-white text-[#BF4528] font-semibold text-sm"
                    >
                      Login
                    </Link>
                  ) : (
                    <button
                      onClick={() => navigate('/dashboard')}
                      className="w-full flex items-center justify-center px-4 py-3 rounded-full bg-orange-600 text-white font-semibold text-sm"
                    >
                      Dashboard
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {studyOpen && (
            <div
              className="hidden xl:block absolute top-[88px] left-0 right-0 px-4 md:px-6 z-[60]"
              onMouseEnter={() => setStudyOpen(true)}
              onMouseLeave={() => setStudyOpen(false)}
            >
              <div className="bg-gradient-to-r from-cyan-200 via-purple-200 to-pink-200 rounded-[28px]  shadow-xl">
                <div className="bg-white rounded-[26px] px-6 py-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-start">
                    {/* Column 1: UK, USA, Canada */}
                    <div className="space-y-3">
                      <Link to="/study-in-uk" className="w-full text-left rounded-xl border border-gray-200 px-4 py-3 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <img src="/images/icons/uk-logo.png" alt="UK" className="h-6 w-6 rounded-sm" />
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">Study in UK</div>
                          <div className="text-xs text-gray-500">100+ Universities</div>
                        </div>
                      </Link>
                      <Link to="/study-in-usa" className="w-full text-left rounded-xl border border-gray-200 px-4 py-3 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <img src="/images/icons/usa-logo.png" alt="USA" className="h-6 w-6 rounded-sm" />
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">Study in USA</div>
                          <div className="text-xs text-gray-500">100+ Universities</div>
                        </div>
                      </Link>
                      <Link to="/study-in-canada" className="w-full text-left rounded-xl border border-gray-200 px-4 py-3 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <img src="/images/icons/ca-logo.png" alt="Canada" className="h-6 w-6 rounded-sm" />
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">Study in Canada</div>
                          <div className="text-xs text-gray-500">100+ Universities</div>
                        </div>
                      </Link>
                    </div>
                    {/* Column 2: Australia, New Zealand, UAE */}
                    <div className="space-y-3">
                      <Link to="/study-in-australia" className="w-full text-left rounded-xl border border-gray-200 px-4 py-3 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <img src="/images/icons/aus-logo.png" alt="Australia" className="h-6 w-6 rounded-sm" />
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">Study in Australia</div>
                          <div className="text-xs text-gray-500">100+ Universities</div>
                        </div>
                      </Link>
                      <Link to="/study-in-new-zealand" className="w-full text-left rounded-xl border border-gray-200 px-4 py-3 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <img src="/images/icons/nz-logo.png" alt="New Zealand" className="h-6 w-6 rounded-sm" />
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">Study in New Zealand</div>
                          <div className="text-xs text-gray-500">100+ Universities</div>
                        </div>
                      </Link>
                      <Link to="/study-in-uae" className="w-full text-left rounded-xl border border-gray-200 px-4 py-3 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <img src="/images/icons/uae-logo.png" alt="UAE" className="h-6 w-6 rounded-sm" />
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">Study in Dubai (UAE)</div>
                          <div className="text-xs text-gray-500">100+ Universities</div>
                        </div>
                      </Link>
                    </div>
                    {/* Column 3: Ireland, Germany, Europe */}
                    <div className="space-y-3">
                      <Link to="/study-in-ireland" className="w-full text-left rounded-xl border border-gray-200 px-4 py-3 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <img src="/images/country/ireland/IE-Ireland.png" alt="Ireland" className="h-6 w-6 rounded-sm object-cover" />
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">Study in Ireland</div>
                          <div className="text-xs text-gray-500">100+ Universities</div>
                        </div>
                      </Link>
                      <Link to="/study-in-germany" className="w-full text-left rounded-xl border border-gray-200 px-4 py-3 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <img src="/images/country/germancy/flag.png" alt="Germany" className="h-6 w-6 rounded-sm object-cover" />
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">Study in Germany</div>
                          <div className="text-xs text-gray-500">100+ Universities</div>
                        </div>
                      </Link>
                      <Link to="/study-in-europe" className="w-full text-left rounded-xl border border-gray-200 px-4 py-3 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <img src="/images/country/europe/contents.png" alt="Europe" className="h-6 w-6 rounded-sm object-cover" />
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">Study in Europe</div>
                          <div className="text-xs text-gray-500">100+ Universities</div>
                        </div>
                      </Link>
                    </div>
                    {/* Column 4: Latest Events */}
                    <div className="space-y-3">
                      <div className="text-xs font-semibold text-gray-500">LATEST EVENTS</div>
                      <Link
                        to={latestPosts.event ? `/event-details/${latestPosts.event.id}` : '/events'}
                        className="block rounded-2xl overflow-hidden border border-gray-200 shadow-sm group"
                      >
                        <img
                          src={resolveUploadsUrl(latestPosts.event?.cover_image) || '/images/about-bg.png'}
                          alt={latestPosts.event?.title || 'Latest Events'}
                          className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        {latestPosts.event?.title ? (
                          <div className="px-3 py-2 text-sm font-semibold text-[#0B1C33] line-clamp-2 group-hover:text-[#FF5722] transition-colors">
                            {latestPosts.event.title}
                          </div>
                        ) : null}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {coachingOpen && (
            <div
              className="hidden xl:block absolute top-[88px] left-0 right-0 px-4 md:px-6 z-[60]"
              onMouseEnter={() => setCoachingOpen(true)}
              onMouseLeave={() => setCoachingOpen(false)}
            >
              <div className="bg-gradient-to-r from-cyan-200 via-purple-200 to-pink-200 rounded-[28px]  shadow-xl">
                <div className="bg-white/70 backdrop-blur-xl rounded-[26px] px-6 py-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
                    {/* Column 1: Coaching */}
                    <div className="space-y-3">
                      <div className="text-sm font-bold uppercase tracking-wide text-gray-600">COACHING</div>
                      {[
                        { to: '/coaching/ielts', label: 'IELTS' },
                        { to: '/coaching/pte', label: 'PTE' },
                        { to: '/coaching/toefl', label: 'TOEFL iBT' },
                        { to: '/coaching/celpip', label: 'CELPIP' },
                        { to: '/coaching/duolingo', label: 'DUOLINGO' },
                        { to: '/coaching/inter-preparatory', label: 'Inter. Preparatory' },
                      ].map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={() => setCoachingOpen(false)}
                          className="w-full text-left rounded-xl border border-gray-200 px-4 py-4 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3"
                        >
                          <span className="h-8 w-8 shrink-0 rounded-full bg-gradient-to-r from-[#F68870] to-[#E05C3E] inline-flex items-center justify-center shadow-md">
                            <img src="/images/icons/Vector6.png" alt="" className="h-4 w-4 object-contain brightness-0 invert" />
                          </span>
                          <div>
                            <div className="text-sm font-bold text-[#0B1C33]">{item.label}</div>
                            <div className="text-xs text-gray-500">Interaction Sub line Here</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                    {/* Column 2: Competitive Exam Preparation */}
                    <div className="flex h-full min-h-0 flex-col space-y-3">
                      <div className="text-sm font-bold uppercase tracking-wide text-gray-600">COMPETITIVE EXAM PREPARATION</div>
                      <div className="relative min-h-0 flex-1 overflow-hidden rounded-2xl border border-gray-200 shadow-sm">
                        <img src="/images/who_we_are.png" alt="" className="absolute inset-0 h-full w-full object-cover" />
                      </div>
                      <div className="space-y-3">
                        {[
                          { to: '/coaching/sat', label: 'SAT' },
                          { to: '/coaching/gre', label: 'GRE' },
                          { to: '/coaching/gmat', label: 'GMAT' },
                        ].map((item) => (
                          <Link
                            key={item.to}
                            to={item.to}
                            onClick={() => setCoachingOpen(false)}
                            className="w-full text-left rounded-xl border border-gray-200 px-4 py-4 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3"
                          >
                            <span className="h-8 w-8 shrink-0 rounded-full bg-gradient-to-r from-[#F68870] to-[#E05C3E] inline-flex items-center justify-center shadow-md">
                              <img src="/images/icons/Vector6.png" alt="" className="h-4 w-4 object-contain brightness-0 invert" />
                            </span>
                            <div>
                              <div className="text-sm font-bold text-[#0B1C33]">{item.label}</div>
                              <div className="text-xs text-gray-500">Interaction Sub line Here</div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                    {/* Column 3: Language Learning */}
                    <div className="flex h-full min-h-0 flex-col space-y-3">
                      <div className="text-sm font-bold uppercase tracking-wide text-gray-600">LANGUAGE LEARNING</div>
                      <div className="relative min-h-0 flex-1 overflow-hidden rounded-2xl border border-gray-200 shadow-sm">
                        <img src="/images/about-bg.png" alt="" className="absolute inset-0 h-full w-full object-cover" />
                      </div>
                      <div className="space-y-3">
                        {[
                          { to: '/coaching/french', label: 'French Language' },
                          { to: '/coaching/german', label: 'German Language' },
                          { to: '/coaching/japanese', label: 'Japanese Language' },
                        ].map((item) => (
                          <Link
                            key={item.to}
                            to={item.to}
                            onClick={() => setCoachingOpen(false)}
                            className="w-full text-left rounded-xl border border-gray-200 px-4 py-4 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3"
                          >
                            <span className="h-8 w-8 shrink-0 rounded-full bg-gradient-to-r from-[#F68870] to-[#E05C3E] inline-flex items-center justify-center shadow-md">
                              <img src="/images/icons/Vector6.png" alt="" className="h-4 w-4 object-contain brightness-0 invert" />
                            </span>
                            <div>
                              <div className="text-sm font-bold text-[#0B1C33]">{item.label}</div>
                              <div className="text-xs text-gray-500">Interaction Sub line Here</div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {servicesOpen && (
            <div
              className="hidden xl:block absolute top-[88px] left-0 right-0 px-4 md:px-6 z-[60]"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <div className="bg-gradient-to-r from-cyan-200 via-purple-200 to-pink-200 rounded-[28px]  shadow-xl">
                <div className="bg-[#FFFFFFB2] shadow-[0px_4px_4px_0px_#00000014] rounded-[26px] px-6 py-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
                    <div className="space-y-3">
                      <Link to="/services/education-loan" className="w-full text-left rounded-xl border border-gray-200 px-5 py-4 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <span className="h-8 w-8 shrink-0 rounded-full bg-[#F47B5F] inline-flex items-center justify-center shadow-md">
                          <img src="/images/icons/money_loan_bag.png" alt="Education Loan" className="h-4 w-4 object-contain" />
                        </span>
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">Education Loan</div>
                          <div className="text-xs text-gray-500">Avail now, pay later</div>
                        </div>
                      </Link>
                      <Link to="/services/sop" className="w-full text-left rounded-xl border border-gray-200 px-5 py-4 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <span className="h-8 w-8 shrink-0 rounded-full bg-[#F47B5F] inline-flex items-center justify-center shadow-md">
                          <img src="/images/icons/Vector-(9).png" alt="Statement of Purpose" className="h-4 w-4 object-contain" />
                        </span>
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">Statement of Purpose</div>
                          <div className="text-xs text-gray-500">Interaction Sub line Here</div>
                        </div>
                      </Link>
                      <Link to="/services/sim-card" className="w-full text-left rounded-xl border border-gray-200 px-5 py-4 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <span className="h-8 w-8 shrink-0 rounded-full bg-[#F47B5F] inline-flex items-center justify-center shadow-md">
                          <img src="/images/icons/sim_card.png" alt="Sim Card" className="h-4 w-4 object-contain" />
                        </span>
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">Sim Card</div>
                          <div className="text-xs text-gray-500">Stay connected with who cares</div>
                        </div>
                      </Link>
                    </div>
                    <div className="space-y-3">
                      <Link to="/services/scholarship" className="w-full text-left rounded-xl border border-gray-200 px-5 py-4 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <span className="h-8 w-8 shrink-0 rounded-full bg-[#F47B5F] inline-flex items-center justify-center shadow-md">
                          <img src="/images/icons/Vector-(7).png" alt="Scholarship" className="h-4 w-4 object-contain" />
                        </span>
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">Scholarship</div>
                          <div className="text-xs text-gray-500">Create scholarship opportunities</div>
                        </div>
                      </Link>
                      <button className="w-full text-left rounded-xl border border-gray-200 px-5 py-4 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <span className="h-8 w-8 shrink-0 rounded-full bg-[#F47B5F] inline-flex items-center justify-center shadow-md">
                          <img src="/images/icons/Vector-(10).png" alt="Psychometric Test" className="h-4 w-4 object-contain" />
                        </span>
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">Psychometric Test</div>
                          <div className="text-xs text-gray-500">Heading Subline</div>
                        </div>
                      </button>
                      <Link to="/services/air-ticket" className="w-full text-left rounded-xl border border-gray-200 px-5 py-4 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <span className="h-8 w-8 shrink-0 rounded-full bg-[#F47B5F] inline-flex items-center justify-center shadow-md">
                          <img src="/images/icons/Vector-(12).png" alt="Air tickets" className="h-4 w-4 object-contain" />
                        </span>
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">Air tickets</div>
                          <div className="text-xs text-gray-500">Get the best deals and offers</div>
                        </div>
                      </Link>
                    </div>
                    <div className="space-y-3">
                      <Link to="/services/insurance" className="w-full text-left rounded-xl border border-gray-200 px-5 py-4 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <span className="h-8 w-8 shrink-0 rounded-full bg-[#F47B5F] inline-flex items-center justify-center shadow-md">
                          <img src="/images/icons/Vector-(13).png" alt="Insurance" className="h-4 w-4 object-contain" />
                        </span>
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">Insurance</div>
                          <div className="text-xs text-gray-500">Secure your travel and study</div>
                        </div>
                      </Link>
                      <Link to="/services/visa-service" className="w-full text-left rounded-xl border border-gray-200 px-5 py-4 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <span className="h-8 w-8 shrink-0 rounded-full bg-[#F47B5F] inline-flex items-center justify-center shadow-md">
                          <img src="/images/icons/Vector-(11).png" alt="Visa Assistance" className="h-4 w-4 object-contain" />
                        </span>
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">Visa Assistance</div>
                          <div className="text-xs text-gray-500">Friendly visa assistance</div>
                        </div>
                      </Link>
                      <Link to="/services/forex" className="w-full text-left rounded-xl border border-gray-200 px-5 py-4 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <span className="h-8 w-8 shrink-0 rounded-full bg-[#F47B5F] inline-flex items-center justify-center shadow-md">
                          <img src="/images/icons/Vector-(8).png" alt="Forex" className="h-4 w-4 object-contain" />
                        </span>
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">Forex</div>
                          <div className="text-xs text-gray-500">Currency exchange facility available</div>
                        </div>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {companyOpen && (
            <div
              className="hidden xl:block absolute top-[88px] left-0 right-0 px-4 md:px-6 z-[60]"
              onMouseEnter={() => setCompanyOpen(true)}
              onMouseLeave={() => setCompanyOpen(false)}
            >
              <div className="bg-gradient-to-r from-cyan-200 via-purple-200 to-pink-200 rounded-[28px]  shadow-xl">
                <div className="bg-white rounded-[26px] px-6 py-6">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-start">
                    {/* Column 1: Navigation Links */}
                    <div className="space-y-3">
                      <Link to="/about-us" className="w-full text-left rounded-xl border border-gray-200 px-4 py-3 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <svg className="h-5 w-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">About US</div>
                          <div className="text-xs text-gray-500">Who we are?</div>
                        </div>
                      </Link>
                      <Link to="/career" className="w-full text-left rounded-xl border border-gray-200 px-4 py-3 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <svg className="h-5 w-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">Career</div>
                          <div className="text-xs text-gray-500">Join our team</div>
                        </div>
                      </Link>
                      <Link to="/contact-us" className="w-full text-left rounded-xl border border-gray-200 px-4 py-3 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <svg className="h-5 w-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">Contact Us</div>
                          <div className="text-xs text-gray-500">Get in touch</div>
                        </div>
                      </Link>
                    </div>

                    {/* Column 2: News */}
                    <div className="space-y-3">
                      
                      <Link to="/news" className="block rounded-2xl overflow-hidden border border-gray-200 shadow-sm group">
                        <img src={resolveUploadsUrl(latestPosts.news?.cover_image) || '/images/who_we_are.png'} alt="News" className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300" />
                       
                      </Link>
                      <Link to="/news" className="w-full text-left rounded-xl border border-gray-200 px-4 py-3 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <svg className="h-5 w-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">News</div>
                          <div className="text-xs text-gray-500">Latest updates</div>
                        </div>
                      </Link>
                    </div>

                    {/* Column 3: Blogs */}
                    <div className="space-y-3">
                      
                      <Link to="/blogs" className="block rounded-2xl overflow-hidden border border-gray-200 shadow-sm group">
                        <img src={resolveUploadsUrl(latestPosts.blog?.cover_image) || '/images/create_account.webp'} alt="Blogs" className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300" />
                        
                      </Link>
                      <Link to="/blogs" className="w-full text-left rounded-xl border border-gray-200 px-4 py-3 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <svg className="h-5 w-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 20l9-5-9-5-9 5 9 5zm0-13l9 5-9 5-9-5 9-5z" /></svg>
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">Blogs</div>
                          <div className="text-xs text-gray-500">Latest posts</div>
                        </div>
                      </Link>
                    </div>

                    {/* Column 4: Events */}
                    <div className="space-y-3">
                      
                      <Link to="/events" className="block rounded-2xl overflow-hidden border border-gray-200 shadow-sm group">
                        <img src={resolveUploadsUrl(latestPosts.event?.cover_image) || '/images/about-bg.png'} alt="Events" className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300" />
                       
                      </Link>
                      <Link to="/events" className="w-full text-left rounded-xl border border-gray-200 px-4 py-3 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <svg className="h-5 w-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3h14v18H5zM8 7h8" /></svg>
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">Events</div>
                          <div className="text-xs text-gray-500">Upcoming</div>
                        </div>
                      </Link>
                    </div>

                    {/* Column 5: Webinar */}
                    <div className="space-y-3">
                      
                      <Link to="/webinar" className="block rounded-2xl overflow-hidden border border-gray-200 shadow-sm group">
                        <img src={resolveUploadsUrl(latestPosts.webinar?.cover_image) || '/images/hero-2.webp'} alt="Webinar" className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300" />
                        
                      </Link>
                      <Link to="/webinar" className="w-full text-left rounded-xl border border-gray-200 px-4 py-3 bg-white hover:bg-orange-50 transition-colors flex items-center gap-3">
                        <svg className="h-5 w-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7 4-7 4V8zm8 0l10 5-10 5V8z" /></svg>
                        <div>
                          <div className="text-sm font-semibold text-[#0B1C33]">Webinar</div>
                          <div className="text-xs text-gray-500">Watch now</div>
                        </div>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Header;
